import { useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { IconTriangleWarningFill18 } from "nucleo-ui-fill-18"
import { z } from "zod"

import { primaryAddress } from "@productlane/lib/src/email-settings"
import {
	AlertDialog,
	Form,
	FormInput,
	SubmitButton,
	useZodForm,
} from "@productlane/ui"

import type { EmailInboxSettingsProps } from "../types"
import { toast } from "@/components/toast"
import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "@/constants"
import { api } from "@/lib/api"
import { useSession } from "@/lib/session"
import { SettingsContainer } from "../../settings-switches/settings-container"

const FormSchema = z.object({
	primaryEmail: z
		.string()
		.email()
		.max(EMAIL_MAXIMUM_LENGTH, EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE),
})

export function PrimaryEmailStep({ workspace }: EmailInboxSettingsProps) {
	const isPrimaryEmailActive = primaryAddress(
		workspace.emailSettings,
	)?.primaryEmail
	const navigate = useNavigate()
	const utils = api.useUtils()
	const [isConfirmOpen, setIsConfirmOpen] = useState(false)
	const [pendingEmail, setPendingEmail] = useState<string | null>(null)
	const activateEmailMutation = api.mail.activateEmailIntegration.useMutation({
		onSuccess: async () => {
			await utils.workspace.info.invalidate()
			const settingsScrollContainer = document.getElementById(
				"settings-scroll-container",
			)
			if (settingsScrollContainer) {
				settingsScrollContainer.scrollTop = settingsScrollContainer.scrollHeight
			}
		},
		onError: (error) => {
			if (error.data?.code === "FORBIDDEN") {
				toast.error({
					title: "Paid plan required",
					description: "Upgrade your workspace to set up a shared email.",
					action: {
						label: "Go to billing",
						callback: () => {
							void navigate({ to: "/settings/plans" })
						},
					},
				})
			} else {
				toast.error({
					title: "Couldn't set up your support email",
					description:
						error.data?.code === "INTERNAL_SERVER_ERROR" || !error.message
							? "Something went wrong on our side. Try again, or contact us if it keeps failing."
							: error.message,
					icon: IconTriangleWarningFill18,
					iconVariant: "warning",
				})
			}

			const email = form.getValues("primaryEmail")
			form.reset({
				primaryEmail: email,
			})
		},
	})
	const editPrimaryEmailMutation = api.mail.changePrimaryEmail.useMutation({
		onSuccess: async () => {
			toast.success({
				title: "Updated primary email",
				description:
					"Email configuration has been reset. Please complete the setup steps again.",
			})
			await utils.workspace.info.invalidate()
		},
		onError: () => {
			toast.error({
				title: "Failed to update primary email",
				description: "Please try again later or try a different email address.",
			})

			const email = form.getValues("primaryEmail")
			form.reset({
				primaryEmail: email,
			})
		},
	})
	const { session } = useSession()
	const form = useZodForm({
		schema: FormSchema,
		defaultValues: {
			primaryEmail: primaryAddress(workspace.emailSettings)
				? primaryAddress(workspace.emailSettings)!.primaryEmail
				: "",
		},
	})
	return (
		<>
			<AlertDialog
				isOpen={isConfirmOpen}
				onOpenChange={setIsConfirmOpen}
				title="Change primary email?"
				description="Changing your primary email will reset your email configuration and require you to complete all setup steps again, including DNS configuration and email forwarding setup."
				confirmText="Change email"
				onCancel={() => {
					setPendingEmail(null)
				}}
				onConfirm={async () => {
					if (pendingEmail) {
						await editPrimaryEmailMutation
							.mutateAsync({ primaryEmail: pendingEmail })
							.catch(() => undefined)
						setPendingEmail(null)
					}
				}}
			/>
			<div className="mb-2 font-semibold text-primary">Set your email</div>
			<div className="mb-4 text-sm text-secondary">
				Choose your support email address that you will receive and send emails
				with.
			</div>
			<SettingsContainer className="space-y-0 divide-y-0">
				<Form
					form={form}
					onSubmit={async (values) => {
						if (isPrimaryEmailActive) {
							setPendingEmail(values.primaryEmail)
							setIsConfirmOpen(true)
						} else {
							// onError already shows why; awaiting keeps the button busy.
							await activateEmailMutation
								.mutateAsync({ primaryEmail: values.primaryEmail })
								.catch(() => undefined)
						}
					}}
				>
					<div className="flex items-start gap-2">
						<FormInput
							placeholder={`hello@${session?.email.split("@")[1]}`}
							name="primaryEmail"
						/>
						<SubmitButton className="h-8 flex-shrink-0">
							{isPrimaryEmailActive ? "Update" : "Save and continue"}
						</SubmitButton>
					</div>
				</Form>
			</SettingsContainer>
		</>
	)
}
